import _ from 'lodash';
import { Reducer } from 'redux';
import { persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import {
  FLIGHTS_SEARCH_REQUEST,
  FLIGHTS_SEARCH_SUCCESSFUL,
  FLIGHTS_SEARCH_FAILURE,
  FLIGHTS_RESULT_SUCCESSFUL,
  FLIGHTS_RESULT_REQUEST,
  FLIGHTS_RESULT_FAILURE,
  FLIGHTS_CLEAR,
  FLIGHTS_RESULT_CLEAR,
  FLIGHTS_TAB_FILTER,
  FLIGHTS_DIRECT_FILTER,
  FLIGHTS_FILTER_CLEAR,
  FLIGHTS_SET_TRAVELER,
  FLIGHTS_SEARCH_PARAMETERS,
  FLIGHTS_EXCHANGE_PARAMETERS,
  FLIGHTS_EXCHANGE_REQUEST,
  FLIGHTS_EXCHANGE_SUCCESSFUL,
  FLIGHTS_EXCHANGE_FAILURE,
  FLIGHTS_EXCHANGE_CLEAR,
  FLIGHTS_EXCHANGE_OPTIONS,
  FLIGHTS_EXCHANGE_RESULT_SUCCESSFUL,
  FLIGHTS_EXCHANGE_RESULT_REQUEST,
  FLIGHTS_EXCHANGE_RESULT_FAILURE,
  FLIGHTS_EXCHANGE_RESULT_CLEAR,
} from 'action-creators/flights';
import { RESET } from 'action-creators/generic';
import { TimeOption } from 'components/FlightsSearch/components/FlightTimePicker/models';
import { Airport } from 'models/airport';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { FlightExchangeState, FlightExchangeStatus } from 'models/flightExchange';
import { FlightExchangeResponse } from 'models/flightExchange';
import { FlightFilter } from 'models/flightFilter';
import { FlightSearchResponse, FlightResultRequestOrderCriteriaEnum } from 'models/flightSearch';
import { FlightTabFilter } from 'models/flightTabFilter';
import { TravelArrangerUsersAllowed } from 'services/userService';

export interface FlightsState {
  departurePoint?: Airport;
  arrivalPoint?: Airport;
  departureDate?: Date;
  returnDate?: Date;
  departureTime?: TimeOption;
  returnTime?: TimeOption;
  cabin?: string;
  traveler?: TravelArrangerUsersAllowed;
  baggageIncluded: boolean;
  directFlight: boolean;
  isLoadingSearch: boolean;
  isLoadingResults: boolean;
  isEnd: boolean;
  orderCriteria: FlightResultRequestOrderCriteriaEnum;
  filters?: FlightFilter;
  flightTabFilter?: FlightTabFilter[];
  search?: FlightSearchResponse;
  error?: string;
  branchCompany?: GetAllBillingCompanyResponse;
  changeFlight: FlightExchangeState;
  changeFlightSearch?: FlightExchangeResponse;
  changeFlightStatus: FlightExchangeStatus;
}

const initialState: FlightsState = {
  baggageIncluded: false,
  directFlight: false,
  isLoadingSearch: false,
  isLoadingResults: false,
  isEnd: false,
  cabin: 'Economy',
  orderCriteria: FlightResultRequestOrderCriteriaEnum.PRICE,
  changeFlight: {},
  changeFlightStatus: {
    isLoadingSearch: false,
    isLoadingResults: false,
    isEnd: false,
    orderCriteria: FlightResultRequestOrderCriteriaEnum.PRICE,
  },
};

const reducer: Reducer<FlightsState> = (state = initialState, action) => {
  switch (action.type) {
    case FLIGHTS_DIRECT_FILTER:
      return {
        ...state,
        isDirectFlightsFilterChecked: action.payload,
      };
    case FLIGHTS_TAB_FILTER:
      return {
        ...state,
        flightTabFilter: action.payload.airFilterTabs,
      };
    case FLIGHTS_SEARCH_PARAMETERS:
      return {
        ...state,
        isLoadingSearch: false,
        isEnd: false,
        filters: undefined,
        search: undefined,
        orderCriteria: FlightResultRequestOrderCriteriaEnum.PRICE,
        ...action.payload,
      };
    case FLIGHTS_SEARCH_REQUEST:
      return {
        ...state,
        isLoadingSearch: true,
        isEnd: false,
        filters: undefined,
        orderCriteria: FlightResultRequestOrderCriteriaEnum.PRICE,
        ...action.payload,
      };
    case FLIGHTS_SEARCH_SUCCESSFUL:
      return {
        ...state,
        isLoadingSearch: false,
        search: {
          ...action.payload,
          results: [],
        },
      };
    case FLIGHTS_SEARCH_FAILURE:
      return {
        ...state,
        error: action.payload,
        search: undefined,
        isLoadingSearch: false,
      };
    case FLIGHTS_RESULT_REQUEST:
      return {
        ...state,
        isLoadingResults: true,
        isEnd: false,
        orderCriteria: action.payload.orderCriteria,
        filters: action.payload.filters || state.filters,
        search: {
          ...state.search,
          results:
            state.orderCriteria !== action.payload.orderCriteria || action.payload.filters ? [] : state.search?.results,
        },
      };
    case FLIGHTS_RESULT_SUCCESSFUL:
      return {
        ...state,
        isLoadingResults: false,
        isEnd: action.payload.length === 0,
        search: {
          ...state.search,
          results: _.unionWith([...(state.search?.results || [])], [...(action.payload || [])], _.isEqual),
        },
      };
    case FLIGHTS_RESULT_FAILURE:
      return {
        ...state,
        error: action.payload,
        isLoadingResults: false,
      };
    case FLIGHTS_RESULT_CLEAR:
      return {
        ...state,
        search: {
          ...state.search,
          results: [],
        },
      };
    case FLIGHTS_FILTER_CLEAR:
      return {
        ...state,
        filters: undefined,
        orderCriteria: FlightResultRequestOrderCriteriaEnum.PRICE,
      };
    case FLIGHTS_CLEAR:
      return {
        ...state,
        search: undefined,
        departurePoint: undefined,
        arrivalPoint: undefined,
        departureDate: undefined,
        returnDate: undefined,
        baggageIncluded: false,
        directFlight: false,
        cabin: 'Economy',
        departureTime: undefined,
        returnTime: undefined,
        orderCriteria: FlightResultRequestOrderCriteriaEnum.PRICE,
        branchCompany: undefined,
        changeFlight: undefined,
      };
    case FLIGHTS_SET_TRAVELER:
      return {
        ...state,
        traveler: action.payload,
      };
    case FLIGHTS_EXCHANGE_PARAMETERS:
      return {
        ...state,
        changeFlight: {
          ...state.changeFlight,
          ...action.payload,
        },
        changeFlightStatus: {
          ...state.changeFlightStatus,
          isLoadingSearch: false,
          isLoadingResults: false,
          error: undefined,
          isEnd: false,
        },
        changeFlightSearch: {
          ...state.changeFlightSearch,
          results: [],
        },
      };
    case FLIGHTS_EXCHANGE_OPTIONS:
      return {
        ...state,
        changeFlight: {
          ...state.changeFlight,
          changeDeparture: action.payload.changeDeparture,
          changeArrival: action.payload.changeArrival,
          departureCity: state.changeFlight.originalFlight?.flightDepartureCity ?? state.changeFlight.departureCity,
          arrivalCity: state.changeFlight.originalFlight?.flightCity ?? state.changeFlight.arrivalCity,
          departureCode: state.changeFlight.originalFlight?.departurePoint ?? state.changeFlight.departureCode,
          arrivalCode: state.changeFlight.originalFlight?.arrivalPoint ?? state.changeFlight.arrivalCode,
          departureDate: state.changeFlight.originalFlight?.date ?? state.changeFlight.departureDate,
          arrivalDate: state.changeFlight.originalFlight?.returnDate ?? state.changeFlight.arrivalDate,
        },
        changeFlightStatus: {
          ...state.changeFlightStatus,
          isLoadingResults: false,
          isLoadingSearch: false,
          error: undefined,
          isEnd: false,
        },
        changeFlightSearch: {
          ...state.changeFlightSearch,
          results: [],
        },
      };
    case FLIGHTS_EXCHANGE_REQUEST:
      return {
        ...state,
        orderCriteria: action.payload.orderCriteria,
        filters: action.payload.filters || state.filters,
        changeFlightStatus: {
          ...state.changeFlightStatus,
          isLoadingSearch: true,
          isEnd: false,
        },
        changeFlightSearch: {
          ...state.changeFlightSearch,
          results:
            state.orderCriteria !== action.payload.orderCriteria || action.payload.filters
              ? []
              : state.changeFlightSearch?.results,
        },
      };
    case FLIGHTS_EXCHANGE_SUCCESSFUL:
      return {
        ...state,
        changeFlightStatus: {
          ...state.changeFlightStatus,
          isLoadingSearch: false,
        },
        changeFlightSearch: {
          ...state.changeFlightSearch,
          ...action.payload,
          results: [],
        },
      };
    case FLIGHTS_EXCHANGE_FAILURE:
      return {
        ...state,
        changeFlightStatus: {
          ...state.changeFlightStatus,
          error: action.payload,
          isLoadingResults: false,
        },
      };
    case FLIGHTS_EXCHANGE_CLEAR:
      return {
        ...state,
        changeFlight: {},
        changeFlightStatus: {
          isLoadingSearch: false,
          isLoadingResults: false,
          isEnd: false,
        },
        changeFlightSearch: {
          ...state.changeFlightSearch,
          results: [],
        },
      };
    case FLIGHTS_EXCHANGE_RESULT_REQUEST:
      return {
        ...state,
        changeFlightStatus: {
          ...state.changeFlightStatus,
          isLoadingResults: true,
          isEnd: false,
          orderCriteria: action.payload.orderCriteria,
          filters: action.payload.filters || state.filters,
        },
        changeFlightSearch: {
          ...state.changeFlightSearch,
          results:
            state.orderCriteria !== action.payload.orderCriteria || action.payload.filters
              ? []
              : state.changeFlightSearch?.results,
        },
      };
    case FLIGHTS_EXCHANGE_RESULT_SUCCESSFUL:
      return {
        ...state,
        changeFlightStatus: {
          ...state.changeFlightStatus,
          isLoadingResults: false,
          isEnd: action.payload.length === 0,
        },
        changeFlightSearch: {
          ...state.changeFlightSearch,
          results: _.unionWith([...(state.changeFlightSearch?.results || [])], [...(action.payload || [])], _.isEqual),
        },
      };
    case FLIGHTS_EXCHANGE_RESULT_FAILURE:
      return {
        ...state,
        changeFlightStatus: {
          ...state.changeFlightStatus,
          error: action.payload,
          isLoadingResults: false,
        },
      };
    case FLIGHTS_EXCHANGE_RESULT_CLEAR:
      return {
        ...state,
        changeFlightSearch: {
          ...state.changeFlightSearch,
          results: [],
        },
      };
    case RESET:
      return initialState;
    default:
      return state;
  }
};

const migrations = {
  2: (state: any) => ({
    ...state,
    cabin: 'Economy',
  }),
};

const persistConfig = {
  storage,
  version: 2,
  key: 'flights',
  blacklist: ['isLoadingResults', 'isLoadingSearch', 'error', 'changeFlightStatus'],
  migrate: createMigrate(migrations),
};

export default persistReducer(persistConfig, reducer);
